/**
 * File uploads for the A2A agent chat surface.
 *
 * Files go through agentic-backend's session-authenticated v1 endpoints
 * (`POST /api/v1/orgs/{orgId}/agents/{agentId}/files[/upload-url]`), which
 * accept the same credentials as the chat path — X-User-Token for OTP
 * sessions, Authorization Bearer for Auth0 — so uploads work in both auth
 * modes. (The A2A gateway's JSON-RPC surface is bearer/JWT-only and
 * rejected OTP sessions.) The backend registers the resulting `file_id`
 * in the gateway's Redis artifact registry — the only registry the chat
 * orchestrator's `resolve_uploaded_file` / `start_workflow` file
 * auto-wrapping reads from. Two paths by size:
 *
 *   * <= 10 MB — inline base64 upload.
 *   * <= 50 MB — presigned-PUT handshake, then a direct S3 PUT of the
 *     raw bytes.
 *
 * The chat message itself carries no structured file field; the
 * orchestrator expects the returned `file_id` UUIDs pasted into the
 * message text (see `formatAgentMessageWithAttachments`).
 */

import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';

export const AGENT_FILE_INLINE_MAX_BYTES = 10 * 1024 * 1024;
export const AGENT_FILE_MAX_BYTES = 50 * 1024 * 1024;

export interface UploadedAgentFile {
  fileId: string;
  filename: string;
  sizeBytes: number;
  contentType: string;
}

interface AgentFileUploadResponse {
  file_id?: string;
}

interface AgentFileUploadUrlResponse {
  artifact_id?: string;
  upload_url?: string;
  headers?: Record<string, string>;
}

const agentFilesUrl = (orgId: string, agentId: string): string =>
  `/api/v1/orgs/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/files`;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const uploadInline = async (
  orgId: string,
  agentId: string,
  file: File,
): Promise<UploadedAgentFile> => {
  const contentType = file.type || 'application/octet-stream';
  const contentBase64 = await fileToBase64(file);

  const response = await apiRequest<AgentFileUploadResponse>({
    url: agentFilesUrl(orgId, agentId),
    method: 'POST',
    target: 'workflow',
    body: {
      filename: file.name,
      content_base64: contentBase64,
      content_type: contentType,
    },
  });

  const fileId = response.data?.file_id;
  if (response.detail || !fileId) {
    throw new Error(response.detail || 'File upload returned no file_id');
  }
  return {
    fileId,
    filename: file.name,
    sizeBytes: file.size,
    contentType,
  };
};

const uploadViaPresignedPut = async (
  orgId: string,
  agentId: string,
  file: File,
): Promise<UploadedAgentFile> => {
  const mimeType = file.type || 'application/octet-stream';

  const response = await apiRequest<AgentFileUploadUrlResponse>({
    url: `${agentFilesUrl(orgId, agentId)}/upload-url`,
    method: 'POST',
    target: 'workflow',
    body: {
      filename: file.name,
      mime_type: mimeType,
      size_bytes: file.size,
    },
  });

  const artifactId = response.data?.artifact_id;
  const uploadUrl = response.data?.upload_url;
  if (response.detail || !artifactId || !uploadUrl) {
    throw new Error(
      response.detail || 'Upload handshake returned no artifact_id/upload_url',
    );
  }

  const putHeaders =
    response.data?.headers && typeof response.data.headers === 'object'
      ? response.data.headers
      : { 'Content-Type': mimeType };

  // Raw fetch — apiRequest would prepend the proxy path and attach our
  // auth header, both of which invalidate the presigned-URL signature.
  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: putHeaders,
    body: file,
  });
  if (!putResponse.ok) {
    const detail = await putResponse.text().catch(() => '');
    throw new Error(
      `Direct S3 upload failed: ${putResponse.status}` +
        (detail ? ` — ${detail.slice(0, 200)}` : ''),
    );
  }

  return {
    fileId: artifactId,
    filename: file.name,
    sizeBytes: file.size,
    contentType: mimeType,
  };
};

export const uploadAgentChatFile = async (
  orgId: string,
  agentId: string,
  file: File,
): Promise<UploadedAgentFile> => {
  if (file.size > AGENT_FILE_MAX_BYTES) {
    throw new Error(
      `${file.name} is larger than the ${Math.floor(AGENT_FILE_MAX_BYTES / 1024 / 1024)} MB limit`,
    );
  }
  try {
    if (file.size <= AGENT_FILE_INLINE_MAX_BYTES) {
      return await uploadInline(orgId, agentId, file);
    }
    return await uploadViaPresignedPut(orgId, agentId, file);
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        context: 'upload-agent-chat-file',
        orgId,
        agentId,
        fileName: file.name,
        fileSize: file.size,
      },
    });
    throw error;
  }
};

/**
 * Bind uploaded files to a chat turn. The A2A chat path has no
 * structured attachment field — the orchestrator resolves bare
 * `file_id` UUIDs found in the message text (via its
 * `resolve_uploaded_file` tool) and passes them to workflow file
 * inputs by id.
 */
export const formatAgentMessageWithAttachments = (
  message: string,
  attachments: Pick<UploadedAgentFile, 'fileId' | 'filename'>[],
): string => {
  if (attachments.length === 0) return message;
  const lines = attachments.map(
    (a) => `- ${a.filename} (file_id: ${a.fileId})`,
  );
  const manifest = `Attached files:\n${lines.join('\n')}`;
  return message ? `${message}\n\n${manifest}` : manifest;
};
